<p-toast [style]="{marginTop: '80px'}"></p-toast>

<!-- ============================================================= -->
<!--  STANDALONE SEARCH PANEL                                       -->
<!--  Only shown when used as a route (/best-fit-list).            -->
<!--  Hidden when embedded inside the tape chart.                  -->
<!-- ============================================================= -->
@if (!isEmbedded) {
  <div class="bfl-page">

    <!-- ── Page header ─────────────────────────────────────────────────────── -->
    <div class="bfl-header">
      <i class="pi pi-star-fill bfl-header-icon"></i>
      <h1 class="bfl-title">Best Fit Finder</h1>
      <span class="bfl-subtitle">Find the best available windows across all properties</span>
      <button pButton class="p-button-text p-button-rounded bfl-help-btn"
              (click)="openHelp()"
              title="How to use Best Fit Finder">
        <i class="pi pi-question-circle"></i>
      </button>
      <!-- Close button — only visible when opened as a MatDialog from inbox -->
      @if (dialogData) {
        <button class="bfl-dialog-close-btn" mat-dialog-close title="Close Best Fit Finder">
          <i class="pi pi-times"></i>
        </button>
      }
    </div>

    <!-- ── Flexible dates toggle (above the shared search template) ────────── -->
    <div class="bfl-flex-toggle-row">
      <label class="bfl-flex-toggle-label">
        <input type="checkbox" [(ngModel)]="flexibleMode" (change)="selectedNights = []">
        <i class="pi pi-calendar-plus"></i>
        Flexible dates — find any N nights within a date range
      </label>
      @if (flexibleMode) {
        <span class="bfl-flex-hint">
          <i class="pi pi-info-circle"></i>
          Select desired night counts in the <strong>Num Nights</strong> filter below, then search.
        </span>
      }
    </div>

    <!-- ── Shared search template — same component used by Tape Chart ──────── -->
    <div class="bfl-search-panel">
      <app-search-template
        #bflSearchTpl
        searchLabel="Find Best Fit"
        [showSearchButton]="true"
        [reset]="true"
        [showStartDate]="true"
        [showEndDate]="true"
        [showDevelopments]="true"
        [showBedrooms]="true"
        [showViewTypes]="true"
        [showLocations]="true"
        [showSleeps]="true"
        [showListingTypes]="true"
        [showShortTermRentals]="true"
        [showYieldMaxProperties]="false"
        [showRateBuckets]="false"
        [showSeasons]="false"
        [showReservationStatuses]="false"
        [showSaleDateStart]="false"
        [showSaleDateEnd]="false"
        [showSaleYear]="false"
        [showNumNts]="flexibleMode"
        [preSetNumNts]="prefilledNights"
        [startDate]="prefilledStartDate"
        [endDate]="prefilledEndDate"
        [displayRecordsOnPageLoad]="false"
        (doSearchCB)="onSearchTemplateSubmit($event)">
      </app-search-template>

      @if (searching) {
        <div class="bfl-searching-bar">
          <i class="pi pi-spin pi-spinner"></i>&nbsp;Searching properties…
        </div>
      }
      @if (searchError) {
        <div class="bfl-error">
          <i class="pi pi-exclamation-triangle"></i> {{ searchError }}
        </div>
      }
    </div>

    <!-- Empty state after a search with no results -->
    @if (!searching && flexWindows.length === 0 && _tapeChartsByProperty?.length === 0 && currentSearchForm?.startDate) {
      <div class="bfl-no-data">
        <i class="pi pi-calendar-times"></i>
        No properties found for those dates. Try widening the date range or removing filters.
      </div>
    }
  </div>
}

<!-- ============================================================= -->
<!--  RESULTS PANEL                                                  -->
<!--  Shared between embedded and standalone modes.                  -->
<!-- ============================================================= -->
@if (flexSearching || flexSearchingInput) {
  <div class="flex-loading">
    <i class="pi pi-spin pi-spinner"></i>&nbsp;Scanning properties for available windows…
  </div>
}

@if (!flexSearching && !flexSearchingInput && flexWindows.length === 0 && isEmbedded
     && activeContext?.flexMode && activeContext?.flexNumNights) {
  <div class="flex-empty">
    <i class="pi pi-info-circle"></i>
    No {{ activeContext.flexNumNights }}-night windows available
    @if (activeContext.resortLabel) { at {{ activeContext.resortLabel }} }
    @if (activeContext.bedrooms) { for {{ activeContext.bedrooms }}-bedroom units }
    in that range. Try extending the range, reducing nights,
    @if (activeContext.bedrooms) { changing bedrooms, }
    or removing the resort filter.
  </div>
}

@if (!flexSearching && !flexSearchingInput && flexWindows.length > 0) {

  <!-- ── Global search + action bar ─────────────────────────────────── -->
  <div class="fw-toolbar">
    <span class="fw-global-wrap p-input-icon-left">
      <i class="pi pi-search"></i>
      <input pInputText type="text" class="fw-global-input"
             [(ngModel)]="flexGlobalFilter" placeholder="Search all columns…">
    </span>

    @if (hasFlexFilters) {
      <button pButton class="p-button-sm p-button-text fw-clear-filters-btn"
              (click)="clearFlexFilters()" title="Clear all filters">
        <i class="pi pi-filter-slash"></i> Clear filters
      </button>
    }

    <span class="fw-result-count">
      {{ displayedFlexWindows.length }} of {{ flexWindows.length }} windows
    </span>

    @if (selectedFlexCount > 0) {
      <span class="fw-action-label">
        <i class="pi pi-check-square"></i>
        {{ selectedFlexCount }} selected
      </span>
      <button pButton class="p-button-sm p-button-success fw-action-btn"
              (click)="buildSelectedQuotes()">
        <i class="pi pi-file-edit"></i>&nbsp;Build Quote{{ selectedFlexCount > 1 ? ' (' + selectedFlexCount + ')' : '' }}
      </button>
      <button pButton class="p-button-sm p-button-text fw-action-clear"
              (click)="selectedFlexKeys.clear()">
        <i class="pi pi-times"></i>
      </button>
    }

    <!-- Re-scan + Help buttons (right side of toolbar) -->
    <span style="margin-left:auto; display:inline-flex; align-items:center; gap:4px;">
      @if (isEmbedded) {
        <button pButton class="p-button-sm p-button-text"
                (click)="runFlexFinder()" title="Re-scan with current data">
          <i class="pi pi-refresh"></i>&nbsp;Re-scan
        </button>
      }
      <button pButton class="p-button-sm p-button-text bfl-help-btn-toolbar"
              (click)="openHelp()"
              title="How to use Best Fit Finder">
        <i class="pi pi-question-circle"></i>&nbsp;How to use
      </button>
    </span>
  </div>

  <!-- ── Results table ────────────────────────────────────────────────── -->
  <div class="fw-table-wrap">
    <table class="fw-table">
      <thead>
        <!-- ── Sort header row ── -->
        <tr>
          <th class="fw-col-chk">
            <input type="checkbox" [checked]="allFlexSelected"
                   (change)="toggleSelectAllFlex($event)" title="Select / deselect all shown">
          </th>
          <th class="fw-col-img"></th>
          <th class="fw-col-fit fw-sortable" (click)="sortFlexBy('fit')">
            Fit
            <i class="pi" [class.pi-sort-alt]="flexSortField!=='fit'"
                          [class.pi-sort-amount-down]="flexSortField==='fit'&&flexSortDir==='desc'"
                          [class.pi-sort-amount-up-alt]="flexSortField==='fit'&&flexSortDir==='asc'"></i>
          </th>
          <th class="fw-col-tot fw-sortable" (click)="sortFlexBy('total')">
            Total w/tax
            <i class="pi" [class.pi-sort-alt]="flexSortField!=='total'"
                          [class.pi-sort-amount-down]="flexSortField==='total'&&flexSortDir==='desc'"
                          [class.pi-sort-amount-up-alt]="flexSortField==='total'&&flexSortDir==='asc'"></i>
          </th>
          <th class="fw-col-checkin fw-sortable" (click)="sortFlexBy('checkin')">
            Check-in
            <i class="pi" [class.pi-sort-alt]="flexSortField!=='checkin'"
                          [class.pi-sort-amount-down]="flexSortField==='checkin'&&flexSortDir==='desc'"
                          [class.pi-sort-amount-up-alt]="flexSortField==='checkin'&&flexSortDir==='asc'"></i>
          </th>
          <th class="fw-col-checkout fw-sortable" (click)="sortFlexBy('checkout')">
            Check-out
            <i class="pi" [class.pi-sort-alt]="flexSortField!=='checkout'"
                          [class.pi-sort-amount-down]="flexSortField==='checkout'&&flexSortDir==='desc'"
                          [class.pi-sort-amount-up-alt]="flexSortField==='checkout'&&flexSortDir==='asc'"></i>
          </th>
          <th class="fw-col-nights fw-sortable" (click)="sortFlexBy('nights')">
            Nts
            <i class="pi" [class.pi-sort-alt]="flexSortField!=='nights'"
                          [class.pi-sort-amount-down]="flexSortField==='nights'&&flexSortDir==='desc'"
                          [class.pi-sort-amount-up-alt]="flexSortField==='nights'&&flexSortDir==='asc'"></i>
          </th>
          <th class="fw-col-prop fw-sortable" (click)="sortFlexBy('prop')">
            Property
            <i class="pi" [class.pi-sort-alt]="flexSortField!=='prop'"
                          [class.pi-sort-amount-down]="flexSortField==='prop'&&flexSortDir==='desc'"
                          [class.pi-sort-amount-up-alt]="flexSortField==='prop'&&flexSortDir==='asc'"></i>
          </th>
          <th class="fw-col-gap-before fw-sortable" (click)="sortFlexBy('gapBefore')"
              title="Empty nights before check-in on this property">
            ←Gap
            <i class="pi" [class.pi-sort-alt]="flexSortField!=='gapBefore'"
                          [class.pi-sort-amount-down]="flexSortField==='gapBefore'&&flexSortDir==='desc'"
                          [class.pi-sort-amount-up-alt]="flexSortField==='gapBefore'&&flexSortDir==='asc'"></i>
          </th>
          <th class="fw-col-gap-after fw-sortable" (click)="sortFlexBy('gapAfter')"
              title="Empty nights after check-out on this property">
            Gap→
            <i class="pi" [class.pi-sort-alt]="flexSortField!=='gapAfter'"
                          [class.pi-sort-amount-down]="flexSortField==='gapAfter'&&flexSortDir==='desc'"
                          [class.pi-sort-amount-up-alt]="flexSortField==='gapAfter'&&flexSortDir==='asc'"></i>
          </th>
          <th class="fw-col-bed fw-sortable" (click)="sortFlexBy('bed')">
            Bed
            <i class="pi" [class.pi-sort-alt]="flexSortField!=='bed'"
                          [class.pi-sort-amount-down]="flexSortField==='bed'&&flexSortDir==='desc'"
                          [class.pi-sort-amount-up-alt]="flexSortField==='bed'&&flexSortDir==='asc'"></i>
          </th>
          <th class="fw-col-bath fw-sortable" (click)="sortFlexBy('bath')">
            Bath
            <i class="pi" [class.pi-sort-alt]="flexSortField!=='bath'"
                          [class.pi-sort-amount-down]="flexSortField==='bath'&&flexSortDir==='desc'"
                          [class.pi-sort-amount-up-alt]="flexSortField==='bath'&&flexSortDir==='asc'"></i>
          </th>
          <th class="fw-col-view fw-sortable" (click)="sortFlexBy('view')">
            View
            <i class="pi" [class.pi-sort-alt]="flexSortField!=='view'"
                          [class.pi-sort-amount-down]="flexSortField==='view'&&flexSortDir==='desc'"
                          [class.pi-sort-amount-up-alt]="flexSortField==='view'&&flexSortDir==='asc'"></i>
          </th>
          <th class="fw-col-act"></th>
        </tr>

        <!-- ── Column filter row ── -->
        <tr class="fw-filter-row">
          <th></th>
          <th></th>
          <th>
            <select class="fw-filter-select" [(ngModel)]="flexFilterFit">
              <option value="">All</option>
              <option value="2">⭐ Best</option>
              <option value="1">✓ Good</option>
              <option value="0">Open</option>
            </select>
          </th>
          <th></th>
          <th>
            <input pInputText type="text" class="fw-filter-input"
                   [(ngModel)]="flexFilterCheckin" placeholder="e.g. Aug">
          </th>
          <th>
            <input pInputText type="text" class="fw-filter-input"
                   [(ngModel)]="flexFilterCheckout" placeholder="e.g. Aug">
          </th>
          <th>
            <select class="fw-filter-select" [(ngModel)]="flexFilterNights">
              <option value="">All</option>
              @for (n of flexUniqueNights; track n) {
                <option [value]="n">{{ n }}nt</option>
              }
            </select>
          </th>
          <th>
            <input pInputText type="text" class="fw-filter-input"
                   [(ngModel)]="flexFilterProp" placeholder="Code / name…">
          </th>
          <th></th>
          <th></th>
          <th>
            <select class="fw-filter-select" [(ngModel)]="flexFilterBed">
              <option value="">All</option>
              @for (b of flexUniqueBeds; track b) {
                <option [value]="b">{{ b }}BR</option>
              }
            </select>
          </th>
          <th>
            <select class="fw-filter-select" [(ngModel)]="flexFilterBath">
              <option value="">All</option>
              @for (b of flexUniqueBaths; track b) {
                <option [value]="b">{{ b }}Ba</option>
              }
            </select>
          </th>
          <th>
            <select class="fw-filter-select" [(ngModel)]="flexFilterView">
              <option value="">All</option>
              @for (v of flexUniqueViews; track v) {
                <option [value]="v">{{ viewTypeLabel(v) }}</option>
              }
            </select>
          </th>
          <th></th>
        </tr>
      </thead>

      <tbody>
        @for (win of displayedFlexWindows.slice(0, flexWindowsLimit); track flexWinKey(win)) {
          <tr class="fw-row"
              [class.fw-row-gap2]="fitTier(win) === 2"
              [class.fw-row-gap1]="fitTier(win) === 1"
              [class.fw-row-selected]="isFlexSelected(win)"
              (click)="toggleFlexSelect(win, $event)">

            <!-- Checkbox -->
            <td class="fw-col-chk" (click)="$event.stopPropagation()">
              <input type="checkbox" [checked]="isFlexSelected(win)"
                     (change)="toggleFlexSelect(win, $event)">
            </td>

            <!-- Thumbnail -->
            <td class="fw-col-img">
              @if (win.thumbImg) {
                <img [src]="win.thumbImg" class="fw-thumb" [alt]="win.propertyCode" loading="lazy">
              } @else {
                <div class="fw-thumb-placeholder"><i class="pi pi-home"></i></div>
              }
            </td>

            <!-- Fit badge -->
            <td class="fw-col-fit">
              @if (fitTier(win) === 2) {
                <span class="fw-badge fw-badge-gap2">
                  <i class="pi pi-star-fill"></i> Best fit
                  <span class="fw-badge-sub">{{ win.fitReasonLabel }}</span>
                  @if (win.fitScore != null) {
                    <span class="fw-badge-score">{{ win.fitScore }}/100</span>
                  }
                </span>
              } @else if (fitTier(win) === 1) {
                <span class="fw-badge fw-badge-gap1">
                  <i class="pi pi-check-circle"></i> Good fit
                  <span class="fw-badge-sub">{{ win.fitReasonLabel }}</span>
                  @if (win.fitScore != null) {
                    <span class="fw-badge-score">{{ win.fitScore }}/100</span>
                  }
                </span>
              } @else {
                <span class="fw-badge fw-badge-gap0">
                  <i class="pi pi-calendar"></i> Open dates
                  @if (win.fitScore != null) {
                    <span class="fw-badge-score">{{ win.fitScore }}/100</span>
                  }
                </span>
              }
            </td>

            <!-- Total w/tax — $/night shown below in place of "incl. tax" label -->
            <td class="fw-col-tot">
              @if (win.pricingStatus === 'ok' && win.dbGrandTotalWithTax != null) {
                <span class="fw-total">{{ win.dbGrandTotalWithTax | currency:'USD':'symbol':'1.0-0' }}</span>
                @if (win.dbPostCouponNightlyRate != null) {
                  <span class="fw-price-label">{{ win.dbPostCouponNightlyRate | currency:'USD':'symbol':'1.2-2' }}/nt</span>
                } @else if (win.dbPerNightAvg != null) {
                  <span class="fw-price-label">{{ win.dbPerNightAvg | currency:'USD':'symbol':'1.0-0' }}/nt</span>
                }
              } @else if (win.pricingStatus === 'pending') {
                <i class="pi pi-spin pi-spinner fw-spinner"></i>
              } @else if (win.preTaxSubtotal != null) {
                <span class="fw-total fw-total-est">{{ win.preTaxSubtotal | currency:'USD':'symbol':'1.0-0' }}</span>
                @if (win.perNightFromPrice != null) {
                  <span class="fw-price-label">
                    {{ win.perNightFromPrice | currency:'USD':'symbol':'1.0-0' }}/nt{{ win.pricingStatus === 'estimate' ? ' est.' : '' }}
                  </span>
                }
              }
              @if (win.pricingStatus === 'error') {
                <span class="fw-price-label" style="color:#c0392b;">
                  <i class="pi pi-exclamation-triangle"></i> unavailable
                </span>
              }
            </td>

            <!-- Check-in -->
            <td class="fw-col-checkin">
              <span class="fw-dates">{{ win.startDate | date:'MMM d, y' }}</span>
            </td>

            <!-- Check-out -->
            <td class="fw-col-checkout">
              <span class="fw-dates">{{ win.endDate | date:'MMM d, y' }}</span>
            </td>

            <!-- Nights -->
            <td class="fw-col-nights">
              <span class="fw-nights">{{ win.numNights }}</span>
            </td>

            <!-- Property -->
            <td class="fw-col-prop">
              <span class="fw-prop-code">{{ win.propertyCode }}</span>
              <span class="fw-prop-dev">{{ win.addressDevelopmentName }}</span>
            </td>

            <!-- Gap before -->
            <td class="fw-col-gap-before"
                title="{{ win.gapNightsBefore }}{{ win.gapBeforeTruncated ? '+' : '' }} empty night(s) before check-in{{ win.gapBeforeTruncated ? ' (at least — fetch window limits visibility)' : '' }}">
              <span class="fw-gap-badge"
                    [class.fw-gap-badge-z]="win.gapNightsBefore === 0"
                    [class.fw-gap-badge-s]="win.gapNightsBefore > 0 && win.gapNightsBefore <= 2"
                    [class.fw-gap-badge-l]="win.gapNightsBefore > 2">
                {{ win.gapNightsBefore }}{{ win.gapBeforeTruncated ? '+' : 'n' }}
              </span>
            </td>

            <!-- Gap after -->
            <td class="fw-col-gap-after"
                title="{{ win.gapNightsAfter }}{{ win.gapAfterTruncated ? '+' : '' }} empty night(s) after check-out{{ win.gapAfterTruncated ? ' (at least — fetch window limits visibility)' : '' }}">
              <span class="fw-gap-badge"
                    [class.fw-gap-badge-z]="win.gapNightsAfter === 0"
                    [class.fw-gap-badge-s]="win.gapNightsAfter > 0 && win.gapNightsAfter <= 2"
                    [class.fw-gap-badge-l]="win.gapNightsAfter > 2">
                {{ win.gapNightsAfter }}{{ win.gapAfterTruncated ? '+' : 'n' }}
              </span>
            </td>

            <!-- Bed -->
            <td class="fw-col-bed">
              @if (win.bedrooms != null) {
                <span class="fw-chip fw-chip-br">{{ win.bedrooms }}</span>
              }
            </td>

            <!-- Bath -->
            <td class="fw-col-bath">
              @if (win.bathrooms != null) {
                <span class="fw-chip fw-chip-ba">{{ win.bathrooms }}</span>
              }
            </td>

            <!-- View -->
            <td class="fw-col-view">
              @if (win.viewType) {
                <span class="fw-chip fw-chip-vw">{{ viewTypeLabel(win.viewType) }}</span>
              }
            </td>

            <!-- Action -->
            <td class="fw-col-act" (click)="$event.stopPropagation()">
              <button pButton class="p-button-sm p-button-outlined fw-quote-btn"
                      (click)="pickFlexWindow(win)">
                <i class="pi pi-file-edit"></i> Quote
              </button>
            </td>

          </tr>
        }
      </tbody>
    </table>
  </div>

  @if (displayedFlexWindows.length > flexWindowsLimit) {
    <div class="flex-more">
      Showing {{ flexWindowsLimit }} of {{ displayedFlexWindows.length }}
      @if (hasFlexFilters) { (filtered from {{ flexWindows.length }}) }
      &nbsp;<a href="javascript:void(0)"
               (click)="flexWindowsLimit = displayedFlexWindows.length">Show all</a>
    </div>
  }
}
